import { InvoiceData, WebhookPayload } from '../types/invoice';
import { fileToBase64 } from '../utils/files';
import { parseNumber } from '../utils/formatters';
import { API_BASE_URL } from '@/constants/config';
import { logger } from '@/lib/logger';

/**
 * Build the complete payload with all invoice data and files in Base64
 */
export const buildWebhookPayload = async (formData: InvoiceData): Promise<WebhookPayload> => {
  // Convert files to Base64
  let xmlFileData = null;
  let pdfFileData = null;

  if (formData.xmlFile) {
    const xmlBase64 = await fileToBase64(formData.xmlFile);
    xmlFileData = {
      name: formData.xmlFile.name,
      content: xmlBase64,
      mimeType: formData.xmlFile.type || 'application/xml',
    };
  }

  if (formData.pdfFile) {
    const pdfBase64 = await fileToBase64(formData.pdfFile);
    pdfFileData = {
      name: formData.pdfFile.name,
      content: pdfBase64,
      mimeType: formData.pdfFile.type || 'application/pdf',
    };
  }

  // Convert credit note files to Base64 (for Pronto Pago)
  let creditNoteXmlData = null;
  let creditNotePdfData = null;

  if (formData.creditNoteXmlFile) {
    const creditNoteXmlBase64 = await fileToBase64(formData.creditNoteXmlFile);
    creditNoteXmlData = {
      name: formData.creditNoteXmlFile.name,
      content: creditNoteXmlBase64,
      mimeType: formData.creditNoteXmlFile.type || 'application/xml',
    };
  }

  if (formData.creditNotePdfFile) {
    const creditNotePdfBase64 = await fileToBase64(formData.creditNotePdfFile);
    creditNotePdfData = {
      name: formData.creditNotePdfFile.name,
      content: creditNotePdfBase64,
      mimeType: formData.creditNotePdfFile.type || 'application/pdf',
    };
  }

  // Convert complement files to Base64 (for PPD invoices)
  let complementXmlData = null;
  let complementPdfData = null;

  if (formData.complementXmlFile) {
    const complementXmlBase64 = await fileToBase64(formData.complementXmlFile);
    complementXmlData = {
      name: formData.complementXmlFile.name,
      content: complementXmlBase64,
      mimeType: formData.complementXmlFile.type || 'application/xml',
    };
  }

  if (formData.complementPdfFile) {
    const complementPdfBase64 = await fileToBase64(formData.complementPdfFile);
    complementPdfData = {
      name: formData.complementPdfFile.name,
      content: complementPdfBase64,
      mimeType: formData.complementPdfFile.type || 'application/pdf',
    };
  }

  // Get email directly
  const fullEmail = formData.email || '';

  // Build structured payload
  // Use expectedWeek (billing week = previous week) for folder naming, not current upload week
  const payload: WebhookPayload = {
    // Metadata
    submittedAt: new Date().toISOString(),
    week: formData.expectedWeek || parseInt(formData.week, 10) || 1,
    year: formData.year || new Date().getFullYear(),
    project: formData.project,

    // Late invoice info
    isLate: formData.isLate || false,
    lateReason: formData.lateReasons?.[0], // Primary reason for backend compatibility
    lateReasons: formData.lateReasons || [],
    lateAcknowledgedAt: formData.lateAcknowledged ? new Date().toISOString() : undefined,

    // Issuer (Emisor)
    issuer: {
      rfc: formData.issuerRfc,
      name: formData.issuerName,
      regime: formData.issuerRegime,
      zipCode: formData.issuerZipCode,
    },

    // Receiver (Receptor)
    receiver: {
      rfc: formData.receiverRfc,
      name: formData.receiverName,
      regime: formData.receiverRegime,
      zipCode: formData.receiverZipCode,
      cfdiUse: formData.cfdiUse,
    },

    // Invoice identification
    invoice: {
      uuid: formData.uuid,
      folio: formData.folio,
      series: formData.series,
      date: formData.invoiceDate,
      certificationDate: formData.certificationDate,
      satCertNumber: formData.satCertNumber,
    },

    // Payment details
    payment: {
      method: formData.paymentMethod,
      form: formData.paymentForm,
      conditions: formData.paymentConditions,
    },

    // Financial summary
    financial: {
      subtotal: parseNumber(formData.subtotal),
      totalTax: parseNumber(formData.totalTax),
      retentionIva: parseNumber(formData.retentionIva),
      retentionIvaRate: formData.retentionIvaRate || 0,
      retentionIsr: parseNumber(formData.retentionIsr),
      retentionIsrRate: formData.retentionIsrRate || 0,
      totalAmount: parseNumber(formData.totalAmount),
      currency: formData.currency,
      exchangeRate: formData.exchangeRate,
    },

    // Payment program (Pronto Pago)
    paymentProgram: {
      program: formData.paymentProgram,
      feeRate: formData.prontoPagoFeeRate,
      feeAmount: formData.prontoPagoFeeAmount,
      netAmount: formData.netPaymentAmount,
    },

    // Line items
    items: formData.items,

    // Contact
    contact: {
      email: fullEmail,
      phone: formData.phoneNumber,
    },

    // Files in Base64
    files: {
      xml: xmlFileData,
      pdf: pdfFileData,
    },

    // Credit Note (required for Pronto Pago)
    creditNote:
      formData.paymentProgram === 'pronto_pago' && formData.creditNoteData
        ? {
            uuid: formData.creditNoteData.uuid,
            folio: formData.creditNoteData.folio,
            series: formData.creditNoteData.series,
            relatedUuid: formData.creditNoteData.relatedUuid,
            tipoRelacion: formData.creditNoteData.tipoRelacion,
            issuerRfc: formData.creditNoteData.issuerRfc,
            issuerName: formData.creditNoteData.issuerName,
            subtotal: formData.creditNoteData.subtotal,
            totalTax: formData.creditNoteData.totalTax,
            totalAmount: formData.creditNoteData.totalAmount,
            currency: formData.creditNoteData.currency,
            issueDate: formData.creditNoteData.issueDate,
            certificationDate: formData.creditNoteData.certificationDate,
            files: {
              xml: creditNoteXmlData,
              pdf: creditNotePdfData,
            },
          }
        : undefined,

    // Payment Complement (for PPD invoices)
    complement:
      formData.hasComplement && formData.complementData
        ? {
            uuid: formData.complementData.uuid,
            folio: formData.complementData.folio,
            series: formData.complementData.series,
            relatedUuids: formData.complementData.relatedUuids,
            issuerRfc: formData.complementData.issuerRfc,
            issuerName: formData.complementData.issuerName,
            paymentDate: formData.complementData.paymentDate,
            paymentForm: formData.complementData.paymentForm,
            amount: formData.complementData.amount,
            currency: formData.complementData.currency,
            partialityNumber: formData.complementData.partialityNumber,
            previousBalance: formData.complementData.previousBalance,
            amountPaid: formData.complementData.amountPaid,
            remainingBalance: formData.complementData.remainingBalance,
            issueDate: formData.complementData.issueDate,
            certificationDate: formData.complementData.certificationDate,
            files: {
              xml: complementXmlData,
              pdf: complementPdfData,
            },
          }
        : undefined,
  };

  return payload;
};

/**
 * API Response interface
 */
interface ApiResponse {
  success: boolean;
  message: string;
  error?: string;
  details?: string[];
  data?: {
    invoiceId?: string;
    uuid?: string;
    driveFolderPath?: string;
    files?: {
      xml?: string;
      pdf?: string;
    };
    existingInvoiceId?: string;
  };
}

/**
 * Check if a UUID already exists in the system
 */
export const checkUuidExists = async (
  uuid: string
): Promise<{ exists: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/validate`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uuid }),
    });

    const data = await response.json();
    return {
      exists: data.exists,
      message: data.message,
    };
  } catch (error) {
    logger.error('UUID check error', { error });
    return {
      exists: false,
      message: 'Error al verificar UUID',
    };
  }
};

/**
 * Submit invoice to the backend API
 */
export const submitInvoice = async (
  formData: InvoiceData
): Promise<{
  success: boolean;
  message: string;
  data?: ApiResponse['data'];
}> => {
  try {
    // Build the complete payload
    const payload = await buildWebhookPayload(formData);

    // Send to backend API
    const response = await fetch(`${API_BASE_URL}/api/invoice`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data: ApiResponse = await response.json();

    if (response.ok && data.success) {
      return {
        success: true,
        message: data.message || '¡Factura enviada correctamente!',
        data: data.data,
      };
    } else if (response.status === 409) {
      // Duplicate invoice
      return {
        success: false,
        message: data.message || 'Esta factura ya fue registrada anteriormente',
        data: data.data,
      };
    } else if (response.status === 400) {
      // Validation error
      const errorDetails = data.details?.join(', ') || 'Datos inválidos';
      return {
        success: false,
        message: `Error de validación: ${errorDetails}`,
      };
    } else {
      logger.error('API error', { status: response.status, data });
      return {
        success: false,
        message:
          data.message || `Error al enviar la factura: Servidor respondió ${response.status}`,
      };
    }
  } catch (error) {
    logger.error('Submission error', { error });
    return {
      success: false,
      message: 'Error de conexión: No se pudo conectar con el servidor. Verifique su conexión.',
    };
  }
};

/**
 * Check API health status
 */
export const checkApiHealth = async (): Promise<{
  healthy: boolean;
  services: { supabase: string; googleDrive: string };
}> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/health`, { credentials: 'include' });
    const data = await response.json();

    return {
      healthy: data.status === 'healthy',
      services: data.services,
    };
  } catch {
    return {
      healthy: false,
      services: { supabase: 'error', googleDrive: 'error' },
    };
  }
};
